import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { fileLabelFromAsset, resolveLandingAssetUrl } from "../../../utils/landingAssets";

export default function AssetUploadField({
  label,
  value,
  helperText = "",
  accept = "image/svg+xml,image/jpeg,image/png,image/webp,image/avif,image/gif,image/x-icon,.svg,.ico",
  kind = "image",
  disabled = false,
  compact = false,
  onUpload,
  onRemove,
}) {
  const inputRef = React.useRef(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const previewUrl = kind === "image" ? resolveLandingAssetUrl(value) : "";

  const selectFile = () => {
    if (!disabled && !busy) inputRef.current?.click();
  };

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setBusy(true);
    setError("");
    try {
      await onUpload(file);
    } catch (uploadError) {
      setError(uploadError?.message || "No se pudo subir el archivo");
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    if (!value || !onRemove) return;
    setBusy(true);
    setError("");
    try {
      await onRemove();
    } catch (removeError) {
      setError(removeError?.message || "No se pudo quitar el archivo");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack spacing={0.8}>
      <Typography variant="body2" sx={{ fontWeight: 900, color: "rgba(25,36,47,.78)" }}>
        {label}
      </Typography>

      <Paper
        variant="outlined"
        sx={{
          p: compact ? 1 : 1.4,
          borderRadius: 2.5,
          borderStyle: "dashed",
          bgcolor: "rgba(25,36,47,.018)",
        }}
      >
        <Stack direction={compact ? "row" : { xs: "column", sm: "row" }} spacing={1.2} alignItems="center">
          {kind === "image" ? (
            <Box
              sx={{
                width: compact ? 76 : 112,
                height: compact ? 58 : 82,
                flex: "0 0 auto",
                borderRadius: 2,
                overflow: "hidden",
                bgcolor: "rgba(25,36,47,.08)",
                display: "grid",
                placeItems: "center",
              }}
            >
              {previewUrl ? (
                <Box component="img" src={previewUrl} alt={label} sx={{ width: "100%", height: "100%", objectFit: "contain" }} />
              ) : (
                <ImageRoundedIcon sx={{ color: "rgba(25,36,47,.38)" }} />
              )}
            </Box>
          ) : (
            <Box sx={{ width: 54, height: 54, borderRadius: 2, bgcolor: "rgba(25,36,47,.07)", display: "grid", placeItems: "center" }}>
              <InsertDriveFileOutlinedIcon sx={{ color: "rgba(25,36,47,.48)" }} />
            </Box>
          )}

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" noWrap sx={{ fontWeight: 850 }}>
              {value ? fileLabelFromAsset(value) : "Todavía no hay un archivo cargado"}
            </Typography>
            {helperText ? (
              <Typography variant="caption" sx={{ color: "rgba(25,36,47,.58)", display: "block", mt: 0.25 }}>
                {helperText}
              </Typography>
            ) : null}
            <Stack direction="row" spacing={0.8} sx={{ mt: 1 }}>
              <Button
                type="button"
                size="small"
                variant="outlined"
                startIcon={busy ? <CircularProgress size={15} /> : <CloudUploadRoundedIcon />}
                disabled={disabled || busy}
                onClick={selectFile}
                sx={{ textTransform: "none", fontWeight: 900, borderRadius: 999 }}
              >
                {value ? "Reemplazar" : "Subir archivo"}
              </Button>
              {value && onRemove ? (
                <IconButton size="small" disabled={disabled || busy} onClick={handleRemove} aria-label={`Quitar ${label}`}>
                  <DeleteOutlineRoundedIcon fontSize="small" />
                </IconButton>
              ) : null}
            </Stack>
          </Box>
        </Stack>
      </Paper>

      <input ref={inputRef} type="file" hidden accept={accept} onChange={handleFile} />
      {error ? <Alert severity="error" sx={{ py: 0 }}>{error}</Alert> : null}
    </Stack>
  );
}
